/**
 * Consent-gated Microsoft Clarity loader.
 *
 * Pages that want session recordings set `window.storyworthClarityProjectId`
 * (via `templates/includes/head-ms-clarity.html`) before this module runs.
 * The Clarity script is only injected once OneTrust reports the Performance
 * group (C0002) as active, and OneTrust's granular consent state is forwarded
 * to Clarity via the consentv2 API — C0002 maps to analytics_Storage (cookies
 * and session stitching) and C0004 to ad_Storage (data sharing with Microsoft
 * Ads) — mirroring the Google Consent Mode mapping in consent.ts. Reference:
 * https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
 */
import { hasAdvertisingConsent, hasAnalyticsConsent } from "./consent"
import { type ClarityFunction } from "./types"

let clarityScriptLoaded = false

export function initializeClarityConsentSync() {
  if (!window.storyworthClarityProjectId) return

  syncClarityConsent()
  window.addEventListener("OneTrustGroupsUpdated", syncClarityConsent)
}

/**
 * Loads Clarity on the first Performance (C0002) grant, then keeps Clarity's
 * consent state in step with OneTrust on every update. On withdrawal the
 * denied consentv2 signal is followed by the legacy `consent, false` call,
 * which additionally erases Clarity's cookies and ends the session.
 * Withdrawal only needs signaling once the script has loaded; before that
 * there is nothing to revoke.
 */
function syncClarityConsent() {
  const projectId = window.storyworthClarityProjectId
  if (!projectId) return

  if (hasAnalyticsConsent()) {
    if (!clarityScriptLoaded) {
      clarityScriptLoaded = true
      loadClarityScript(projectId)
    }
    sendClarityConsentState()
  } else if (clarityScriptLoaded) {
    sendClarityConsentState()
    window.clarity?.("consent", false)
  }
}

function sendClarityConsentState() {
  window.clarity?.("consentv2", {
    analytics_Storage: hasAnalyticsConsent() ? "granted" : "denied",
    ad_Storage: hasAdvertisingConsent() ? "granted" : "denied",
  })
}

/**
 * Injects the Clarity tag, installing the standard command queue stub so
 * `clarity(...)` calls made before the script finishes loading are replayed
 * once it does.
 */
function loadClarityScript(projectId: string) {
  if (!window.clarity) {
    const clarityStub: ClarityFunction = (...args: unknown[]) => {
      clarityStub.q = clarityStub.q || []
      clarityStub.q.push(args)
    }
    window.clarity = clarityStub
  }

  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.clarity.ms/tag/${projectId}`
  document.head.appendChild(script)
}

export function resetClarityForTests() {
  clarityScriptLoaded = false
  window.removeEventListener("OneTrustGroupsUpdated", syncClarityConsent)
}
